import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Stage, Layer, Rect, Group, Text } from 'react-konva';
import { colorToArray, getSoundPropsById, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { Howl } from 'howler';

import Spinner from '../builder/Spinner';
import Konva from 'konva';
import { useImageLoader } from '../../lib/hooks.js';

const item_types = ['ok', 'ko', 'superok', 'superko'];

const standard_board_size = 600;	// px
const default_game_layout = {
	gap:20,
	padding:{
		top:20,
		right:20,
		bottom:20,
		left:20,
	},
};

const default_smash_effects = {
	superok:{
		animation:'shake',
		sound_fx:'success',
		replace_image:null,
	},
	ok:{
		animation:'shake',
		sound_fx:'hint',
		replace_image:null,
	},
	ko:{
		animation:'fall_aside',
		sound_fx:'error_buzzer',
		replace_image:null,
	},
	superko:{
		animation:'slide_up',
		sound_fx:'funny_laugh',
		replace_image:null,
	},
}

const getEffects = (stage_effects) => {
	const effects = {};
	for(let type in default_smash_effects) {
		effects[type] = { ...default_smash_effects[type] };

		if(stage_effects[type] == undefined) continue;
		effects[type] = { ...effects[type], ...stage_effects[type] };
	}

	return effects;
};

const SmashItCanvas = forwardRef((props, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ indexed_items,setIndexedItems ] = useState({});
	const [ reset, setReset ] = useState({ reset:true });
	const [ full_loaded, setFullLoaded ] = useState(false);
	const timer = useRef(null);

	const stage = props?.stage ?? {};
	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const demo_animations = props?.demo_animations ?? false;
	const is_playable = props?.is_playable ?? ((!is_builder || is_test) ? true : false);

	let speed_multiplier = 1;
	switch(stage?.smash_level ?? 'normal') {
		case 'fast':
			speed_multiplier = 0.7;
			break;

		case 'normal':
			speed_multiplier = 1;
			break;

		case 'slow':
			speed_multiplier = 1.3;
			break;
	}

	const board_grid_col = parseInt(stage?.smash_grid ?? 4);
	const board_grid_row = board_grid_col;
	const go_up_duration = 1 * speed_multiplier;		// in sec
	const go_down_duration = 1.5 * speed_multiplier;	// in sec
	const level_increase_delay = 3;			// in sec
	const item_h_proportion = useRef(0.7);	// in % from 0-1 (cover_height_proportion equals to "1 - item_height_propotion")
	const item_foreground_delta = 0.1;		// in % from 0-1 (overlapping between two items)
	const speed_up_factor = 0.90;			// in % from 0-1
	const min_delay_between_items = 1;		// in sec
	const delta_on_min_hit = 0.02;			// in % from 0-1

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const game_layout = stage?.smash_layout ?? default_game_layout;
	const display_outter_border = stage?.display_outter_border ?? true;

	const grid_gap = parseInt(game_layout?.gap ?? default_game_layout.gap);
	const padding = { ...default_game_layout.padding, ...(game_layout?.padding ?? {}) };

	const max_points_for_ok = 50;			// positive
	const max_points_for_superok = 150;		// positive
	const points_for_miss = 20;				// negative
	const points_for_ko = 50;				// negative
	const points_for_superko = 5000;		// negative

	// the probability to get a OK item equals to "1 - superok_probability - superko_probability - ko_probability":
	const superok_probability = 0.1;		// in % from 0-1
	const superko_probability = 0.1;		// in % from 0-1
	const ko_probability = 0.3;				// in % from 0-1

	const game_score = useRef(0);

	const miss_color = colorToArray(stage?.smash_miss_color ?? 'rgba(255, 255, 255, 1)');

	const effects = getEffects(stage?.smash_effects ?? {});

	// We need to set the initial canvas size to the container, at most, to avoid functionality issues on small devices
	// (apparently, when the canvas gets shrinked more than 30-40% at once, some weird things happen)
	// so we initially set the canvas size to the minimum value between the default (600px) and the body (to fit small device screens):
	const size = useRef(Math.min(standard_board_size, document.querySelector('body').offsetWidth));

	const calculateItemSize = () => {
		const size_w = (size.current - parseInt(padding.left) - parseInt(padding.right) - ((board_grid_col - 1) * grid_gap)) / board_grid_col;
		const size_h = (size.current - parseInt(padding.top) - parseInt(padding.bottom) - ((board_grid_row - 1) * grid_gap)) / board_grid_row;

		return { w:size_w, h:size_h };
	};

	const item_size = useRef(calculateItemSize());
	const delay_next = useRef(3);	// sec
	const refs = useRef([]);
	const stoppedGame = useRef(false);
	const animations = useRef({});
	const superko_hitted = useRef(false);
	const items_interval = useRef(null);
	const max_y_to_hit = useRef(item_h_proportion.current * item_size.current.h - 0.1);

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			exitGame();
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	useEffect(() => {
		resetGame();
	}, [ 
		stage.smash_images,
		stage.smash_item_cover_image,
		stage.smash_item_bg_color,
		stage.smash_cover_image,
		stage.smash_grid,
		stage.smash_layout,
	]);

	useEffect(() => {
		item_size.current = calculateItemSize();
		setIndexedItems({ ...indexed_items });
	}, [
		game_layout,
		board_grid_col,
		stage.smash_grid,
	])

	useEffect(() => {
		if(full_loaded) {
			if(refs.current['layer'] != undefined) {
				refs.current['layer'].draw();

				if(is_playable) {
					startAnimations();
				} else if(demo_animations) {
					startDemoAnimations();
				}
			}

			return () => {
				clearTimeout(timer.current);
			}
		}
	}, [ full_loaded ]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			stoppedGame.current = true;

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const cleanup = () => {
				exitGame();

				if(cb != undefined) {
					setTimeout(() => {
						cb(success_game);
					}, 200);
				}
			}
			cleanup();
		},
	}), []);

	/* SOUNDS - ini */
	const sound_ok = (!['', 'none'].includes(effects.ok.sound_fx)) ? new Howl({ ...getSoundPropsById(effects.ok.sound_fx) }) : false;
	const soundOK = () => {
		if(sound_ok === false) return;
		sound_ok.play();
	}

	const sound_ko = (!['', 'none'].includes(effects.ko.sound_fx)) ? new Howl({ ...getSoundPropsById(effects.ko.sound_fx) }) : false;
	const soundKO = () => {
		if(sound_ko === false) return;
		sound_ko.play();
	};

	const sound_superok = (!['', 'none'].includes(effects.superok.sound_fx)) ? new Howl({ ...getSoundPropsById(effects.superok.sound_fx) }) : false;
	const soundSuperOK = () => {
		if(sound_superok === false) return;
		sound_superok.play();
	};

	const sound_superko = (!['', 'none'].includes(effects.superko.sound_fx)) ? new Howl({ ...getSoundPropsById(effects.superko.sound_fx) }) : false;
	const soundSuperKO = () => {
		if(sound_superko === false) return;
		sound_superko.play();
	};
	/* SOUNDS - end */
 
	const exitGame = () => {
		Object.keys(animations.current).map((item) => {
			if(animations.current[item] != undefined) {
				animations.current[item].pause();
				animations.current[item] = null;
			}
		});

		if(refs.current[`main_stage`]) {
			refs.current[`main_stage`].removeChildren();
			refs.current[`main_stage`].destroy();
		}
	};

	const prepareImagesToLoad = () => {
		const images = [];
		if(isNotEmpty(stage, 'smash_bg_image')) {
			images.push({
				id:'full_bg',
				name:'full_bg',
				url:stage.full_smash_bg_image,
				use_full_size:true,
			});
		}
	
		if(isNotEmpty(stage, 'smash_cover_image')) {
			images.push({
				id:'full_fg',
				name:'full_fg',
				url:stage.full_smash_cover_image,
				use_full_size:true,
			});
		}
	
		if(isNotEmpty(stage, 'smash_item_bg_image')) {
			images.push({
				id:'background',
				name:stage.smash_item_bg_image,
				url:stage.full_smash_item_bg_image,
				fit:'cover',
			});
		}
	
		if(isNotEmpty(stage, 'smash_item_cover_image')) {
			images.push({
				id:'foreground',
				name:stage.smash_item_cover_image,
				url:stage.full_smash_item_cover_image,
				fit:'contain',
			});
		}
	
		for(let type in stage.smash_effects) {
			if(stage.smash_effects[type].animation != 'custom') continue;
			if(!isNotEmpty(stage.smash_effects[type], 'full_replace_image')) continue;

			images.push({
				id:`custom_${type}`,
				name:`custom_${type}`,
				url:stage.smash_effects[type].full_replace_image,
				image:stage.smash_effects[type].full_replace_image,
				fit:'cover',
			});
		}

		(stage?.smash_images ?? []).map((image) => {
			if(!isNotEmpty(image, 'image')) return;

			images.push({
				id:image.type,
				name:image.image,
				url:image.full_image,
				image:image.full_image,
				fit:'contain',
			});
		});

		return images;
	};

	const images = useMemo(prepareImagesToLoad, [
		stage.smash_images,
		stage.full_smash_bg_image,
		stage.full_smash_cover_image,
		stage.full_smash_item_bg_image,
		stage.full_smash_item_cover_image,
	]);

	useImageLoader(images, (loaded_images) => {
		const current = {};
		setFullLoaded(false);

		Object.keys(loaded_images).map((image_name) => {
			const image = loaded_images[image_name];

			if(!isNotEmpty(image, 'img')) return;

			const type = image.id;
			const new_image = adaptImageSizes({
				fillPatternImage:image.img,
				fit:image.fit,
				image_w:image.w,
				image_h:image.h,
				use_full_size:image?.use_full_size ?? false,
			});
			if(type == 'foreground') {
				item_h_proportion.current = 1 - new_image.scale * image.h / item_size.current.h;
				max_y_to_hit.current = item_h_proportion.current * item_size.current.h - 0.1;
			}

			if(item_types.includes(type)) {
				if(current[type] == undefined) current[type] = [];
				current[type] = [...current[type],new_image];
				return;
			}

			current[type] = new_image;
		})

		setIndexedItems(current);

		setTimeout(() => {
			gameInit();
		}, 0);
	});

	const calcImageSize = (w, h, container_w, container_h, type = 'cover') => {
		const w_prop = (container_w + 1) / w;
		const h_prop = (container_h + 1) / h;

		if(type == 'contain') return Math.min(w_prop, h_prop);

		return Math.max(w_prop, h_prop);
	};

	const calcItemOffset = (item, _size) => {
		let offset_x = (_size * item.w - item_size.current.w) / 2;
		let offset_y = (_size * item.h - item_size.current.h) / 2;

		return { x:offset_x, y:offset_y };
	};

	const adaptImageSizes = (image) => {
		const container_w = (image?.use_full_size ?? false) ? size.current : item_size.current.w;
		const container_h = (image?.use_full_size ?? false) ? size.current : item_size.current.h;

		const scale_modifier = 1;

		const image_w = image.image_w;
		const image_h = image.image_h;

		const _scale = calcImageSize(image_w, image_h, container_w + scale_modifier, container_h + scale_modifier, image.fit);

		const _offset = calcItemOffset({ w:image_w, h:image_h }, _scale);
	
		return { ...image, scale:_scale, offset:_offset };
	};

	useEffect(() => {
		Object.keys(indexed_items).map((item) => {
			if(Array.isArray(indexed_items[item])) {
				const new_items = [];
				indexed_items[item].map((it) => {
					new_items.push(adaptImageSizes(it));
				});
				indexed_items[item] = new_items;
			} else {
				indexed_items[item] = adaptImageSizes(indexed_items[item]);
			}
		});

		setIndexedItems({ ...indexed_items });
	}, [ item_size.current ]);

	const gameInit = (from_resize) => {
		if(from_resize == undefined) from_resize = false;

		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				item_size.current = calculateItemSize();
				props.startTimer();

				setFullLoaded(true);
			});
		} else {
			setFullLoaded(true);
		}
	};

	const checkIfMounted = (col, row) => {
		// game is unmounted
		if(refs.current[`main_stage`] == undefined) return false;
		if(refs.current[`item_${col}_${row}`] == undefined) return false;
		if(refs.current[`group_item_${col}_${row}`] == undefined) return false;
		return true;
	}

	const checkIfItemIsVisible = (col, row, analyze_y = true, force = false) => {
		if(!checkIfMounted(col, row)) return false;

		if(force) return true;
		if(!analyze_y) return true;

		if(refs.current[`item_${col}_${row}`].attrs.y > max_y_to_hit.current) return false;
		return true;
	};

	/*	Returns a value from 0 to 1 (being 1 the maximum scoring,
		when the item is to the max height, and 0 the value for minimum scoring) */
	const calculateVisibilityPercentage = (col, row) => {
		const direction = refs.current[`item_${col}_${row}`].attrs?.direction ?? 1;

		const coord_y = refs.current[`item_${col}_${row}`].attrs?.y ?? 0;
		const max_coord_y = item_h_proportion.current * item_size.current.h;

		const direction_break_percent = 1;	//If going up, we give the 100% of the points since it's very difficult to achieve

		const percent = direction_break_percent * Math.max(max_coord_y - coord_y, 0) / max_coord_y;

		if(direction < 0) {
			// Going down!
			// Simple decreasing score (from direction_break_percent to 0):
			return Math.max(Math.round(100 * percent) / 100, delta_on_min_hit);	// with 2 decimals
		}

		// Going up!
		// If going up, we give the 100% of the points since it's very difficult to achieve
		return 1;
	};

	const getItemType = (col, row) => {
		const is_super = (refs.current[`item_${col}_${row}`].attrs?.is_super ?? false) ? 'super' : '';
		const is_ok = (refs.current[`item_${col}_${row}`].attrs?.is_ok ?? true) ? 'ok' : 'ko';

		return `${is_super}${is_ok}`;
	};

	const updateScore = (type, percent, col, row) => {
		if(demo_animations) return;

		let new_score = 0;
		let new_score_label;
		switch(type) {
			case 'ok':
				new_score = Math.round(percent * max_points_for_ok);
				new_score_label = `+${new_score}`;
				break;

			case 'superok':
				new_score = Math.round(percent * max_points_for_superok);
				new_score_label = `+${new_score}`;
				break;

			case 'ko':
				new_score = -points_for_ko;
				new_score_label = `${new_score}`;
				break;

			case 'superko':
				new_score = -points_for_superko;
				new_score_label = `${new_score}`;
				break;

			case 'miss':
			default:
				new_score = -points_for_miss;
				new_score_label = `${new_score}`;
				break;
		}

		animateScoreText(new_score_label, col, row);
		game_score.current = Math.max(0, game_score.current + new_score);

		if(props.onScore != undefined) props.onScore(game_score.current)
	};

	const hitOnItem = (col, row, update_score = true, forced_type = false, real_icon = true) => {
		if(!refs.current[`item_${col}_${row}`].attrs.is_enabled && forced_type === false) return;

		refs.current[`item_${col}_${row}`].setAttr('is_enabled', false);
		const type = (forced_type !== false) ? forced_type : getItemType(col, row);
		const original_type = getItemType(col, row);
		//const type = (forced_type !== false) ? forced_type : getItemType(col, row);

		if(update_score) {
			const percent = calculateVisibilityPercentage(col, row);
			updateScore(type, percent, col, row);
		}

		let color_arr = false;
		switch(type) {
			case 'ko':
			case 'superko':
				color_arr = colorToArray(stage?.smash_ko_color ?? 'rgba(210, 210, 210, 1)');
				break;

			case 'ok':
			case 'superok':
			default:
				color_arr = colorToArray(stage?.smash_ok_color ?? 'rgba(120, 195, 0, 1)');
				break;
		}

		switch(type) {
			case 'ok':
				soundOK();
				break;

			case 'ko':
				soundKO();
				break;

			case 'superok':
				soundSuperOK();
				break;

			case 'superko':
				if(real_icon === true) soundSuperKO();
				break;
		}

		applyTint(col, row, parseInt(color_arr[0]) * 1.6, parseInt(color_arr[1]) * 1.6, parseInt(color_arr[2]) * 1.6, 'item');
		applyTint(col, row, color_arr[0], color_arr[1], color_arr[2], 'item_bg');
		applyTint(col, row, parseInt(color_arr[0]) * 1.2, parseInt(color_arr[1]) * 1.2, parseInt(color_arr[2]) * 1.2, 'item_fg');

		if(type == 'superko' && update_score === true) {
			animateItemSuperKO(col, row);
			return;
		}

		switch(effects[original_type].animation) {
			case 'shake':
			default:
				shakeAnimation(col, row);
				break;

			case 'slide_up':
				slideUpAnimation(col, row);
				break;

			case 'fade_out':
				fadeOutAnimation(col, row);
				break;

			case 'fall_aside':
				fallAsideAnimation(col, row);
				break;

			case 'custom':
				customImageAnimation(col, row);
				break;
		}
	};

	const missOnItem = (col, row) => {
		updateScore('miss', null, col, row);

		applyTint(col, row, miss_color[0], miss_color[1], miss_color[2], 'item_bg');
		applyTint(col, row, miss_color[0], miss_color[1], miss_color[2], 'item_fg');

		setTimeout(() => {
			if(!checkIfItemIsVisible(col, row)) {
				clearTint(col, row, 'item_bg');
				clearTint(col, row, 'item_fg');
			}
		}, 500);
	};

	const clickOnItem = (col, row) => {
		if(checkIfItemIsVisible(col, row)) {
			hitOnItem(col, row);
		} else {
			checkIfItemIsVisible(col, row);
			missOnItem(col, row);
		}
	};

	const setItemType = () => {
		const seed = Math.random();
		if((indexed_items?.ko ?? []).length > 0) {
			if(seed <= (ko_probability)) return 'ko';

			if((indexed_items?.superko ?? []).length > 0 && seed <= (ko_probability + superko_probability)) return 'superko';
			if((indexed_items?.superok ?? []).length > 0 && seed <= (ko_probability + superko_probability + superok_probability)) return 'superok';
		} else {
			if((indexed_items?.superko ?? []).length > 0 && seed <= (superko_probability)) return 'superko';
			if((indexed_items?.superok ?? []).length > 0 && seed <= (superko_probability + superok_probability)) return 'superok';
		}

		return 'ok';
	};

	const pickRandItemCoord = (direction) => {
		return Math.floor(Math.random() * ((direction == 'col') ? board_grid_col : board_grid_row));
	};

	const startAnimations = () => {
		animations.current = {};
		delay_next.current = 3;

		setTimeout(() => {
			animateItemUp(pickRandItemCoord('col'), pickRandItemCoord('row'), 'ok');
	
			timer.current = setInterval(() => {
				animateItemUp(pickRandItemCoord('col'), pickRandItemCoord('row'), 'ok');
			}, level_increase_delay * 1000);
		}, 0);
	};

	const startDemoAnimations = () => {
		const min = Math.floor(board_grid_col / 2) - 1;
		const max = board_grid_col - min - 1;

		const available_demo_positions = [
			{ col:min, row:min },
			{ col:max, row:min },
			{ col:min, row:max },
			{ col:max, row:max },
		]

		const has_ko = (stage.smash_images.filter((i) => (i.type == 'ko')).length > 0) ? true : false;
		const has_superok = (stage.smash_images.filter((i) => (i.type == 'superok')).length > 0) ? true : false;
		const has_superko = (stage.smash_images.filter((i) => (i.type == 'superko')).length > 0) ? true : false;

		let index = 0;
		const distribution = {
			ok:available_demo_positions[index],
		};

		if(has_superok) {
			index++;
			distribution.superok = available_demo_positions[index];
		}

		if(has_ko) {
			index++;
			distribution.ko = available_demo_positions[index];
		}

		if(has_superko) {
			index++;
			distribution.superko = available_demo_positions[index];
		}

		setTimeout(() => {
			Object.keys(distribution).map((dis) => {
				refs.current[`group_text_${distribution[dis].col}_${distribution[dis].row}`].setAttrs({
					y:1,
					opacity:1,
				});

				refs.current[`shadow_text_${distribution[dis].col}_${distribution[dis].row}`].setAttrs({
					text:dis.toUpperCase(),
				});

				refs.current[`text_${distribution[dis].col}_${distribution[dis].row}`].setAttrs({
					text:dis.toUpperCase(),
				});

				//refs.current[`group_text_${distribution[dis].col}_${distribution[dis].row}`].cache(default_cache_props);

				animateItemUp(distribution[dis].col, distribution[dis].row, dis);
			});
		}, 0);
	};

	const resetGame = () => {
		if(refs.current['layer'] == undefined) return;

		killAllAnimations();
		clearAllCaches();

		setFullLoaded(false);
		gameInit();
		setFullLoaded(true);

		refs.current['layer'].draw();

		setReset({ ...reset });

		if(is_playable) {
			startAnimations();
		} else if(demo_animations) {
			startDemoAnimations();
		}
	};

	if(is_builder && (indexed_items.ok == undefined || indexed_items.ok.length < 1 || indexed_items.foreground == undefined)) {
		return (
			<div class="canvas_cont game_with_errors">
				{i18n("At least 1 OK item and the base image are required.")}
			</div>
		)
	}

	if(!full_loaded) {
		return (
			<div class="canvas_cont">
				<Spinner>Loading game</Spinner>
			</div>
		)
	}

	const foreground_h = Math.ceil(item_size.current.h * (indexed_items.foreground.image_h / indexed_items.foreground.image_w));
	const size_scale = size.current / standard_board_size;

	const animateScoreText = (score_txt, col, row) => {
		let top_delta = foreground_h;
		let text_selector = `text_${col}_${row}`;
		if(col == undefined || row == undefined) {
			// we hit on the background
			top_delta = 0.1 * size.current;
			text_selector = `global_text`;
		}

		refs.current[`group_${text_selector}`].setAttrs({
			y:0.5 * top_delta,
		});

		refs.current[text_selector].setAttrs({
			text:score_txt,
		});

		refs.current[`shadow_${text_selector}`].setAttrs({
			text:score_txt,
		});

		refs.current[`group_${text_selector}`].cache(default_cache_props);

		refs.current[`group_${text_selector}`].setAttrs({
			opacity:0,
		});

		refs.current[`group_${text_selector}`].to({
			y:0,
			opacity:1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				refs.current[`group_${text_selector}`].to({
					y:-0.5 * top_delta,
					opacity:0,
					duration:go_down_duration - 0.2,
					easing:Konva.Easings.StrongEaseInOut,
				});
			},
		});
	};

	const killAllAnimations = () => {
		clearInterval(items_interval.current);

		[...Array(board_grid_col).keys()].map((item_x, col) => {
			[...Array(board_grid_row).keys()].map((item_y, row) => {
				if(animations.current[`_${col}_${row}`] != undefined) animations.current[`_${col}_${row}`] = null;
			});
		});
	};

	const clearCache = (col, row, target = 'item') => {
		if(!refs.current[`${target}_${col}_${row}`]) return;
		refs.current[`${target}_${col}_${row}`].clearCache();
	};

	const clearAllCaches = () => {
		[...Array(board_grid_col).keys()].map((item_x, col) => {
			[...Array(board_grid_row).keys()].map((item_y, row) => {
				clearCache(col, row, 'item');
				clearCache(col, row, 'item_bg');
				clearCache(col, row, 'item_fg');
			});
		});
	};

	const applyCache = (col, row, target = 'item') => {
		if(!refs.current[`${target}_${col}_${row}`]) return;
		refs.current[`${target}_${col}_${row}`].cache(default_cache_props);
	};

	const clearTint = (col, row, target = 'item') => {
		clearCache(col, row, target);
		applyCache(col, row, target);
		refs.current[`${target}_${col}_${row}`].filters([]);
		refs.current['layer'].draw();
	};

	const applyTint = (col, row, red, green, blue, target = 'item') => {
		clearCache(col, row, target);
		applyCache(col, row, target);
		refs.current[`${target}_${col}_${row}`].filters([Konva.Filters.RGB]);
		refs.current[`${target}_${col}_${row}`].red(Math.min(255, Math.max(0, parseInt(red))));
		refs.current[`${target}_${col}_${row}`].green(Math.min(255, Math.max(0, parseInt(green))));
		refs.current[`${target}_${col}_${row}`].blue(Math.min(255, Math.max(0, parseInt(blue))));
		refs.current['layer'].draw();
	};

	const finishDownAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row, false)) return;

		refs.current[`item_${col}_${row}`].setAttr('is_enabled', true);
		refs.current[`group_item_${col}_${row}`].setAttr('rotation', 0);

		clearTint(col, row, 'item_bg');
		clearTint(col, row, 'item_fg');
		clearTint(col, row, 'item');
		animations.current[`_${col}_${row}`] = null;

		if(!superko_hitted.current) {
			if(!demo_animations) {
				setTimeout(() => {
					animateItemUp(pickRandItemCoord('col'), pickRandItemCoord('row'), setItemType());
					delay_next.current = Math.max(delay_next.current * speed_up_factor, min_delay_between_items);
				}, (delay_next.current * 1000));
			} else {
				setTimeout(() => {
					animateItemUp(col, row, getItemType(col, row));
				}, 500);
			}
		}
	};

	const getRandomItemInType = (type) => {
		if(indexed_items[type] == undefined) return false;
		if(indexed_items[type].length == 1) return 0;
		return Math.floor(Math.random() * indexed_items[type].length);
	};

	const animateItemUp = (col, row, type) => {
		if(!checkIfMounted(col, row)) return;

		const is_item_visible = checkIfItemIsVisible(col, row);
		if(is_item_visible || superko_hitted.current) return;

		const item_index = getRandomItemInType(type);
		if(item_index === false) return;

		const offset = indexed_items[type][item_index].offset;
		const item_props = {
			y:(item_h_proportion.current + 5 * item_foreground_delta) * item_size.current.h,
			easing:Konva.Easings.StrongEaseInOut,
			direction:1,
			fillPatternImage:indexed_items[type][item_index].fillPatternImage,
			fillPatternScaleX:indexed_items[type][item_index].scale,
			fillPatternScaleY:indexed_items[type][item_index].scale,
			is_ok:true,
			is_super:false,
		};

		switch(type) {
			case 'ok':
				item_props.is_ok = true;
				break;

			case 'superok':
				item_props.is_ok = true;
				item_props.is_super = true;
				break;

			case 'ko':
				item_props.is_ok = false;
				break;

			case 'superko':
				item_props.is_ok = false;
				item_props.is_super = true;
				break;
		}

		if(refs.current[`group_item_${col}_${row}`] == undefined) return;
		refs.current[`group_item_${col}_${row}`].setAttrs({
			x:-offset.x + item_size.current.w / 2,
			y:-offset.y + item_size.current.h,
			scaleX:1,
			scaleY:1,
			rotation:0,
			opacity:1,
		});

		if(refs.current[`item_${col}_${row}`] == undefined) return;
		refs.current[`item_${col}_${row}`].setAttrs(item_props);

		clearTint(col, row, 'item');
		clearTint(col, row, 'item_bg');
		clearTint(col, row, 'item_fg');

		refs.current['layer'].draw();

		animations.current[`_${col}_${row}`] = new Konva.Tween({
			node:refs.current[`item_${col}_${row}`],
			y:0,
			easing:Konva.Easings.StrongEaseInOut,
			duration:go_up_duration,
			onFinish:() => {
				if(!demo_animations) {
					animateItemDown(col, row, false, true);

					if(refs.current[`item_${col}_${row}`] != undefined) {
						refs.current[`item_${col}_${row}`].setAttrs({
							direction:-1,
						});
					}
				}
			},
		}).play();
	};

	const animateItemDown = (col, row, is_cb = false, force = false) => {
		if(!checkIfItemIsVisible(col, row, false, force)) return;

		animations.current[`_${col}_${row}`] = new Konva.Tween({
			node:refs.current[`item_${col}_${row}`],
			y:(item_h_proportion.current + 5 * item_foreground_delta) * item_size.current.h,
			easing:Konva.Easings.StrongEaseInOut,
			duration:(!is_cb) ? go_down_duration : 0.01,
			onFinish:() => {
				finishDownAnimation(col, row);
			},
		}).play();
	};

	const shakeAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		refs.current[`item_${col}_${row}`].to({
			y:Math.max(item_size.current.h * 0.1, refs.current[`item_${col}_${row}`].attrs.y - item_size.current.h * 0.2),
			duration:0.1,
			easing:Konva.Easings.BounceEaseInOut,
		});

		refs.current[`group_item_${col}_${row}`].to({
			rotation:-10,
			easing:Konva.Easings.BounceEaseInOut,
			duration:0.1,
			onFinish:() => {
				refs.current[`group_item_${col}_${row}`].to({
					rotation:10,
					easing:Konva.Easings.BounceEaseInOut,
					duration:0.1,
					onFinish:() => {
						refs.current[`group_item_${col}_${row}`].to({
							rotation:0,
							easing:Konva.Easings.BounceEaseInOut,
							duration:0.1,
							onFinish:() => {
								animateItemDown(col, row);
							},
						});
					},
				});
			},
		});
	};

	const shakeOnSuperKO = (col, row, cb) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		refs.current[`item_${col}_${row}`].to({
			y:Math.max(item_size.current.h * 0.1, refs.current[`item_${col}_${row}`].attrs.y - item_size.current.h * 0.2),
			duration:0.05,
			easing:Konva.Easings.BounceEaseInOut,
		});

		refs.current[`whole_item_${col}_${row}`].to({
			rotation:-8,
			easing:Konva.Easings.BounceEaseInOut,
			duration:0.05,
			onFinish:() => {
				refs.current[`whole_item_${col}_${row}`].to({
					rotation:8,
					easing:Konva.Easings.BounceEaseInOut,
					duration:0.05,
					onFinish:() => {
						cb();

						refs.current[`whole_item_${col}_${row}`].to({
							rotation:0,
							easing:Konva.Easings.BounceEaseInOut,
							duration:0.05,
						});
					},
				});
			},
		});
	};

	const slideUpAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		refs.current[`item_${col}_${row}`].to({
			y:-item_size.current.h,
			duration:0.6,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				animateItemDown(col, row, true);
			},
		});
	};

	const fadeOutAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		refs.current[`group_item_${col}_${row}`].to({
			opacity:0,
			duration:0.6,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				animateItemDown(col, row, true);
			},
		});
	};

	const fallAsideAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		refs.current[`item_${col}_${row}`].to({
			y:0,
			duration:0.3,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				refs.current[`group_item_${col}_${row}`].to({
					rotation:-100,
					duration:go_down_duration,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						animateItemDown(col, row, true);
					},
				});
			},
		});
	};

	const customImageAnimation = (col, row) => {
		if(!checkIfItemIsVisible(col, row)) return;
		if(animations.current[`_${col}_${row}`] == undefined) return;

		animations.current[`_${col}_${row}`].pause();

		const type = getItemType(col, row);

		const item_props = {
			fillPatternImage:indexed_items[`custom_${type}`].fillPatternImage,
			fillPatternScaleX:indexed_items[`custom_${type}`].scale,
			fillPatternScaleY:indexed_items[`custom_${type}`].scale,
			y:0,
		};

		if(refs.current[`group_item_${col}_${row}`] == undefined) return;
		refs.current[`group_item_${col}_${row}`].setAttrs({
			x:item_size.current.w / 2,
			y:item_size.current.h,
		});

		refs.current[`item_${col}_${row}`].setAttrs(item_props);

		clearTint(col, row, 'item');

		refs.current['layer'].draw();

		refs.current[`group_item_${col}_${row}`].to({
			scaleX:0,
			scaleY:0,
			y:item_size.current.h / 3,
			duration:go_down_duration,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				animateItemDown(col, row, true);
			},
		});
	};

	const animateItemSuperKO = (col, row) => {
		superko_hitted.current = true;
		clearInterval(items_interval.current);

		if(Object.keys(animations.current).length > 1) {
			Object.keys(animations.current).map((item) => {
				if(animations.current[item] != undefined) animations.current[item].pause();
			});

			let c = 0;

			Object.keys(animations.current).map((item) => {
				if(item != `_${col}_${row}`) {
					const item_part = item.substring(1).split("_");

					if(!refs.current[`item_${item_part[0]}_${item_part[1]}`].attrs.is_enabled) {
						finishDownAnimation(item_part[0], item_part[1]);
						return;
					}

					setTimeout(() => {
						shakeOnSuperKO(item_part[0], item_part[1], () => {
							hitOnItem(item_part[0], item_part[1], false, 'superko', false);
						});
						c++;
					}, (c * 20));
				} else {
					setTimeout(() => {
						hitOnItem(col, row, false, 'superko', false);

						setTimeout(() => {
							superko_hitted.current = false;
							(!demo_animations) ? startAnimations() : startDemoAnimations();
						}, 2 * go_down_duration);
					}, (150 * Object.keys(animations.current).length));
				}
			});
		} else {
			hitOnItem(col, row, false, 'superko', false);

			setTimeout(() => {
				superko_hitted.current = false;
				(!demo_animations) ? startAnimations() : startDemoAnimations();
			}, 2 * go_down_duration);
		}
	};

	// To avoid antialiasign strokes:
	const exact_item_w = Math.ceil(item_size.current.w);
	const exact_item_h = Math.ceil(item_size.current.h);

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
			ref={(node) => { refs.current[`main_stage`] = node; }}
		>
			<Layer ref={(node) => { refs.current[`layer`] = node; }}>
				<Rect
					ref={(node) => { refs.current[`game_bg`] = node; }}
					width={size.current}
					height={size.current}
					fill={stage?.smash_bg_color ?? '#CCCCCC'}
					fillPatternImage={indexed_items?.full_bg?.fillPatternImage ?? null}
					fillPatternRepeat='no-repeat'
					fillPatternScaleX={indexed_items?.full_bg?.scale ?? null}
					fillPatternScaleY={indexed_items?.full_bg?.scale ?? null}
					fillPriority={(isNotEmpty((indexed_items?.full_bg ?? {}), 'fillPatternImage')) ? 'pattern' : 'color'}
				/>

				<Group x={parseInt(padding.left)} y={parseInt(padding.top)}>
					{[...Array(board_grid_col).keys()].map((item_x, col) => {
						return (
							[...Array(board_grid_row).keys()].map((item_y, row) => {
								return (
									<Group
										key={`whole_item_${col}_${row}`}
										ref={(node) => { refs.current[`whole_item_${col}_${row}`] = node; }}
										x={Math.ceil(col * (item_size.current.w + grid_gap) + item_size.current.w / 2)}
										y={Math.ceil(row * (item_size.current.h + grid_gap) + item_size.current.h / 2)}
										offsetX={item_size.current.w / 2}
										offsetY={item_size.current.h / 2}
										filters={[]}
										clipFunc={(context) => {
											context.beginPath();
											context.moveTo(0, 0);
											context.lineTo(exact_item_w, 0);
											context.lineTo(exact_item_w, exact_item_h);
											context.lineTo(0, exact_item_h);
											context.closePath();
										}}
									>
										<Group
											ref={(node) => { refs.current[`item_bg_${col}_${row}`] = node; }}
										>
											<Rect
												width={exact_item_w}
												height={exact_item_h}
												fill={stage?.smash_item_bg_color ?? '#FFFFFF'}
											/>

											{(indexed_items.background != undefined) && (
												<Rect
													width={exact_item_w}
													height={exact_item_h}
													fillPatternImage={indexed_items?.background?.fillPatternImage ?? null}
													fillPatternRepeat='no-repeat'
													fillPatternScaleX={indexed_items?.background?.scale ?? null}
													fillPatternScaleY={indexed_items?.background?.scale ?? null}
												/>
											)}
										</Group>

										<Group
											ref={(node) => { refs.current[`group_item_${col}_${row}`] = node; }}
											x={Math.ceil(item_size.current.w / 2)}
											y={Math.ceil(item_size.current.h) - 2}
											offsetX={item_size.current.w / 2}
											offsetY={item_size.current.h}
											clipFunc={(context) => {
												context.beginPath();
												context.moveTo(2, 0);
												context.lineTo(exact_item_w - 2, 0);
												context.lineTo(exact_item_w - 2, exact_item_h - item_foreground_delta);
												context.lineTo(2, exact_item_h - item_foreground_delta);
												context.closePath();
											}}
										>
											<Rect
												ref={(node) => { refs.current[`item_${col}_${row}`] = node; }}
												y={(item_h_proportion.current + 5 * item_foreground_delta) * item_size.current.h}
												fillPatternImage={indexed_items.ok[0].fillPatternImage}
												fillPatternRepeat='no-repeat'
												fillPatternScaleX={indexed_items.ok[0].scale}
												fillPatternScaleY={indexed_items.ok[0].scale}
												width={exact_item_w + 1}
												height={exact_item_h}
												filters={[]}
												is_enabled={true}
												direction={1}
												is_ok={true}
												is_super={false}
											/>
										</Group>

										<Group
											clipFunc={(context) => {
												context.beginPath();
												context.moveTo(0, 0);
												context.lineTo(exact_item_w, 0);
												context.lineTo(exact_item_w, foreground_h);
												context.lineTo(0, foreground_h);
												context.closePath();
											}}
											y={Math.ceil(exact_item_h - foreground_h) + 1}
										>
											<Rect
												ref={(node) => { refs.current[`item_fg_${col}_${row}`] = node; }}
												width={exact_item_w}
												height={foreground_h}
												fillPatternImage={indexed_items.foreground.fillPatternImage}
												fillPatternRepeat='no-repeat'
												fillPatternScaleX={indexed_items.foreground.scale}
												fillPatternScaleY={indexed_items.foreground.scale}
												fillPatternOffsetX={0}
												fillPatternOffsetY={0}
											/>

											<Group
												y={foreground_h * item_foreground_delta - 1}
											>
												<Group
													ref={(node) => { refs.current[`group_text_${col}_${row}`] = node; }}
													y={0}
												>
													<Text
														ref={(node) => { refs.current[`shadow_text_${col}_${row}`] = node; }}
														width={exact_item_w}
														height={foreground_h}
														align='center'
														verticalAlign='middle'
														fontSize={20 * size_scale}
														fontStyle='bold'
														strokeWidth={5}
														stroke='black'
														lineCap='rounded'
														lineJoin='rounded'
														fill='black'
													/>

													<Text
														ref={(node) => { refs.current[`text_${col}_${row}`] = node; }}
														width={exact_item_w}
														height={foreground_h}
														align='center'
														verticalAlign='middle'
														fontSize={20 * size_scale}
														fontStyle='bold'
														fill='white'
													/>
												</Group>
											</Group>
										</Group>
									</Group>
								)
							})
						)
					})}
				</Group>

				{(indexed_items.full_fg != undefined) && (
					<Rect
						width={size.current}
						height={size.current}
						fillPatternImage={indexed_items.full_fg.fillPatternImage}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={indexed_items.full_fg.scale}
						fillPatternScaleY={indexed_items.full_fg.scale}
						opacity={1}
					/>
				)}

				<Group
					ref={(node) => { refs.current[`group_global_text`] = node; }}
					y={0}
				>
					<Text
						ref={(node) => { refs.current[`shadow_global_text`] = node; }}
						width={size.current}
						height={size.current}
						align='center'
						verticalAlign='middle'
						fontSize={20 * size_scale}
						fontStyle='bold'
						strokeWidth={5}
						stroke='white'
						lineCap='round'
						lineJoin='round'
						fill='white'
					/>

					<Text
						ref={(node) => { refs.current[`global_text`] = node; }}
						width={size.current}
						height={size.current}
						align='center'
						verticalAlign='middle'
						fontSize={20 * size_scale}
						fontStyle='bold'
						fill='black'
					/>
				</Group>

				<Rect
					width={size.current}
					height={size.current}
					onMouseDown={() => updateScore('miss')}		// onClick
					onTouchStart={() => updateScore('miss')}	// onTap
				/>

				<Group x={parseInt(padding.left)} y={parseInt(padding.top)}>
					{[...Array(board_grid_col).keys()].map((item_x, col) => {
						return (
							[...Array(board_grid_row).keys()].map((item_y, row) => {
								return (
									<Group
										key={`active_whole_item_${col}_${row}`}
										x={Math.ceil(col * (item_size.current.w + grid_gap))}
										y={Math.ceil(row * (item_size.current.h + grid_gap))}
										onClick={() => clickOnItem(col, row)}
										onTap={() => clickOnItem(col, row)}
									>
										<Rect
											width={exact_item_w}
											height={exact_item_h}
										/>
									</Group>
								)
							})
						)
					})}
				</Group>
			</Layer>
		</Stage>
	)
})

export default SmashItCanvas;